import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { TrendingUp, DollarSign, Users, Share2, Code, Globe, Zap, CheckCircle, ArrowRight, Building2, Calendar, Target, BarChart3, Phone, Mail, MapPin } from 'lucide-react';
import { site } from '@/data/content';

export default function CaseStudyCodeLab() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'How CodeLab Increased Sales by 47% After Using ZuriHub',
      description: 'Case study: CodeLab, a software development company in Uganda, increased sales by 47% and saved $260/month by switching from Intercom to ZuriHub Enterprise plan.',
      datePublished: '2026-09-15',
      dateModified: '2026-09-15',
      author: {
        '@type': 'Organization',
        name: 'ZuriHub',
        url: site.baseUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: 'ZuriHub',
        logo: {
          '@type': 'ImageObject',
          url: `${site.baseUrl}/og-image.png`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${site.baseUrl}/case-study/codelab`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'CodeLab',
      url: 'https://codelab.co.ug',
      description: 'Software Development company serving enterprise clients with custom software development, domain management, dedicated hosting, USSD development',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'UG',
        addressRegion: 'Kampala',
      },
      sameAs: ['https://codelab.co.ug'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much did CodeLab save by switching to ZuriHub?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'CodeLab saved $260 per month by switching from Intercom ($650/month for 10 agents) to ZuriHub Enterprise plan ($390/month for 10 agents).',
          },
        },
        {
          '@type': 'Question',
          name: 'What results did CodeLab achieve with ZuriHub?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'CodeLab increased sales by 47% after implementing ZuriHub, improved customer response times, and expanded their social media reach using Content Lab.',
          },
        },
        {
          '@type': 'Question',
          name: 'What features does ZuriHub Enterprise include?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'ZuriHub Enterprise includes unified inbox, AI-powered support with Zyra AI, knowledge base, analytics, AI voice, Content Lab for social media management, advanced security, 24/7 support, and custom integrations.',
          },
        },
      ],
    },
  ];

  useEffect(() => {
    document.title = 'CodeLab Case Study: 47% Sales Increase with ZuriHub';
  }, []);

  return (
    <>
      <SEO
        title="CodeLab Case Study: 47% Sales Increase with ZuriHub"
        description="Learn how CodeLab, a software development company in Uganda, increased sales by 47% and saved $260/month by switching from Intercom to ZuriHub Enterprise plan. Discover the results and strategies used."
        path="/case-study/codelab"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-20 md:py-28 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)'
            }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white px-4 py-2 rounded-full mb-6 shadow-lg">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm font-semibold tracking-wide uppercase">Customer Success Story</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
                How CodeLab Increased Sales by <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-400">47%</span> with ZuriHub
              </h1>
              
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed max-w-3xl">
                A software development company in Uganda transformed customer support and social media management while cutting costs by 40%
              </p>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-blue-200">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Building2 className="h-5 w-5" />
                  <span className="font-medium">CodeLab Uganda</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <MapPin className="h-5 w-5" />
                  <span className="font-medium">Kampala, Uganda</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Calendar className="h-5 w-5" />
                  <span className="font-medium">September 2026</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Results */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                icon: 'TrendingUp', 
                value: '47%', 
                label: 'Sales Increase',
                description: 'After implementing ZuriHub',
                color: 'from-emerald-500 to-green-500'
              },
              { 
                icon: 'DollarSign', 
                value: '$260', 
                label: 'Monthly Savings',
                description: 'vs previous Intercom plan',
                color: 'from-blue-500 to-indigo-500'
              },
              { 
                icon: 'Users', 
                value: '10', 
                label: 'Support Agents',
                description: 'Fully equipped team',
                color: 'from-purple-500 to-pink-500'
              },
            ].map((stat, i) => {
              const StatIcon = stat.icon === 'TrendingUp' ? TrendingUp : stat.icon === 'DollarSign' ? DollarSign : Users;
              return (
                <div key={i} className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
                  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-r ${stat.color} text-white mb-4`}>
                    <StatIcon className="h-7 w-7" />
                  </div>
                  <div className="text-4xl font-extrabold text-slate-900 mb-2">{stat.value}</div>
                  <div className="text-lg font-semibold text-slate-700 mb-1">{stat.label}</div>
                  <div className="text-sm text-slate-500">{stat.description}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Article */}
            <div className="lg:col-span-2 space-y-10">
              {/* Company Overview */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Building2 className="h-8 w-8 text-brand-600" />
                  About CodeLab
                </h2>
                
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-8 border border-slate-200">
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">
                    <strong>CodeLab</strong> is a leading software development company based in Uganda, serving enterprise clients across East Africa with comprehensive digital solutions. The company specializes in:
                  </p>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Custom Software Development</strong> - Building tailored solutions for enterprise clients</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Domain Management</strong> - Professional domain registration and management services</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Dedicated Hosting</strong> - Reliable hosting infrastructure for mission-critical applications</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>USSD Development</strong> - Mobile USSD solutions for mass market reach</span>
                    </li>
                  </ul>
                  <div className="mt-6 flex items-center gap-4">
                    <a href="https://codelab.co.ug" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700 font-semibold">
                      <Globe className="h-4 w-4" />
                      Visit CodeLab Website
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>

              {/* The Challenge */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Target className="h-8 w-8 text-red-600" />
                  The Challenge
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  Before implementing ZuriHub, CodeLab was using <strong>Intercom</strong> to manage their customer support operations. With a team of 10 support agents, they were paying <strong>$65 per agent per month</strong>, totaling <strong>$650 monthly</strong> for their customer support platform.
                </p>

                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mt-6">
                  <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Key Challenges Faced
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 font-bold">•</span>
                      <span>High cost structure at $650/month for 10 agents</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Limited social media management capabilities</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Fragmented customer communication across multiple tools</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Difficulty tracking social media ROI and engagement</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* The Solution */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Zap className="h-8 w-8 text-brand-600" />
                  The Solution: ZuriHub Enterprise
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  CodeLab switched to <strong>ZuriHub Enterprise plan</strong> at <strong>$390 per month for 10 agents</strong>, immediately reducing their monthly support costs by <strong>$260 (40% savings)</strong>. But the cost savings were just the beginning.
                </p>

                <div className="bg-gradient-to-r from-brand-50 to-indigo-50 rounded-2xl p-8 border border-brand-200 mt-6">
                  <h3 className="text-xl font-bold text-brand-900 mb-6">Enterprise Features Implemented</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { icon: 'Share2', title: 'Content Lab', desc: 'Multi-platform social media scheduling and publishing' },
                      { icon: 'Users', title: 'Unified Inbox', desc: 'All customer messages in one centralized workspace' },
                      { icon: 'Zap', title: 'Zyra AI', desc: 'AI-powered reply suggestions and conversation summaries' },
                      { icon: 'BarChart3', title: 'Advanced Analytics', desc: 'Real-time performance metrics and reporting' },
                      { icon: 'Phone', title: 'AI Voice', desc: 'Integrated voice calling and transcription' },
                      { icon: 'Code', title: 'Custom Integrations', desc: 'Tailored API integrations for their tech stack' },
                    ].map((feature, i) => {
                      const FeatureIcon = feature.icon === 'Share2' ? Share2 : feature.icon === 'Users' ? Users : feature.icon === 'Zap' ? Zap : feature.icon === 'BarChart3' ? BarChart3 : feature.icon === 'Phone' ? Phone : Code;
                      return (
                        <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-slate-100">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 text-brand-600 flex-shrink-0">
                            <FeatureIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-900">{feature.title}</h4>
                            <p className="text-sm text-slate-600">{feature.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Content Lab Impact */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Share2 className="h-8 w-8 text-purple-600" />
                  Content Lab: Expanding Social Media Reach
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  One of the most impactful features for CodeLab was <strong>Content Lab</strong>, ZuriHub's enterprise social media management tool. This feature enabled CodeLab to:
                </p>

                <div className="space-y-4 mt-6">
                  {[
                    {
                      title: 'Multi-Platform Publishing',
                      description: 'Schedule and publish content across Facebook, LinkedIn, Instagram, X (Twitter), YouTube, and TikTok from a single dashboard',
                      impact: 'Reduced social media management time by 60%'
                    },
                    {
                      title: 'Content Calendar',
                      description: 'Plan and organize content campaigns with visual calendar views and team collaboration',
                      impact: 'Improved content consistency and brand messaging'
                    },
                    {
                      title: 'Analytics & ROI Tracking',
                      description: 'Track engagement metrics, reach, and conversion across all social platforms',
                      impact: 'Data-driven decisions for content strategy'
                    },
                    {
                      title: 'Team Collaboration',
                      description: 'Multiple team members can collaborate on content creation, approval, and publishing',
                      impact: 'Streamlined workflow and faster time-to-publish'
                    },
                  ].map((item, i) => (
                    <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                      <h4 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h4>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-sm font-semibold">
                        <CheckCircle className="h-4 w-4" />
                        {item.impact}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* The Results */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                  The Results
                </h2>
                
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
                  <h3 className="text-2xl font-bold text-green-900 mb-6">Measurable Business Impact</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-green-600 text-white text-2xl font-bold">
                        47%
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-900">Sales Increase</h4>
                        <p className="text-gray-600">After implementing ZuriHub and Content Lab</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-600 text-white text-2xl font-bold">
                        $260
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-900">Monthly Cost Savings</h4>
                        <p className="text-gray-600">Reduced from $650 to $390 per month</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-600 text-white text-2xl font-bold">
                        60%
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-900">Faster Response Times</h4>
                        <p className="text-gray-600">Due to unified inbox and AI assistance</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-600 text-white text-2xl font-bold">
                        3x
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-900">Social Media Reach</h4>
                        <p className="text-gray-600">Expanded audience through Content Lab</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cost Comparison */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <DollarSign className="h-8 w-8 text-green-600" />
                  Cost Comparison: Intercom vs ZuriHub
                </h2>
                
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-lg">
                  <table className="w-full">
                    <thead className="bg-slate-900 text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">Feature</th>
                        <th className="px-6 py-4 text-center font-semibold">Intercom</th>
                        <th className="px-6 py-4 text-center font-semibold">ZuriHub Enterprise</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      <tr className="bg-slate-50">
                        <td className="px-6 py-4 font-semibold text-slate-900">Monthly Cost (10 agents)</td>
                        <td className="px-6 py-4 text-center text-slate-700">$650</td>
                        <td className="px-6 py-4 text-center text-green-600 font-bold">$390</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-slate-700">Unified Inbox</td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="px-6 py-4 text-slate-700">AI-Powered Support</td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-slate-700">Social Media Management</td>
                        <td className="px-6 py-4 text-center text-red-600">✗</td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="px-6 py-4 text-slate-700">Content Lab</td>
                        <td className="px-6 py-4 text-center text-red-600">✗</td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-slate-700">AI Voice</td>
                        <td className="px-6 py-4 text-center text-red-600">✗</td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="px-6 py-4 text-slate-700">Advanced Analytics</td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-slate-700">24/7 Support</td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                        <td className="px-6 py-4 text-center"><CheckCircle className="h-5 w-5 text-green-600 mx-auto" /></td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="bg-green-50 p-6 text-center">
                    <p className="text-lg font-bold text-green-900">
                      Total Annual Savings: <span className="text-2xl">$3,120</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Testimonial */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">What CodeLab Says</h2>
                
                <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-2xl p-8 text-white relative overflow-hidden">
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                  
                  <div className="relative">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 text-white text-3xl font-bold shadow-xl">
                          CL
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="mb-4">
                          <svg className="h-8 w-8 text-white/30" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                          </svg>
                        </div>
                        <p className="text-xl italic text-blue-100 mb-6 leading-relaxed">
                          "Switching to ZuriHub was a game-changer for us. Not only did we save $260 per month, but the Content Lab feature helped us expand our social media reach significantly. Our sales increased by 47% within the first few months of implementation. The unified inbox and AI features have made our support team much more efficient."
                        </p>
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="font-bold text-white text-lg">CodeLab Team</div>
                            <div className="text-blue-200">Software Development Company, Uganda</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-brand-600 to-indigo-600 rounded-2xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Achieve Similar Results?</h3>
                <p className="text-lg text-blue-100 mb-6 max-w-2xl mx-auto">
                  Join CodeLab and hundreds of other businesses using ZuriHub to transform their customer experience and drive growth.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/book-a-demo"
                    className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-brand-600 transition hover:bg-slate-100"
                  >
                    Book a Demo
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-white px-8 py-4 text-base font-bold text-white transition hover:bg-white/10"
                  >
                    View Pricing
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Company Info Card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-brand-600" />
                  Company Details
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-slate-500">Website</div>
                      <a href="https://codelab.co.ug" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700 font-semibold">
                        codelab.co.ug
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-slate-500">Location</div>
                      <div className="text-slate-900 font-semibold">Kampala, Uganda</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-slate-500">Team Size</div>
                      <div className="text-slate-900 font-semibold">10 Support Agents</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Code className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-slate-500">Industry</div>
                      <div className="text-slate-900 font-semibold">Software Development</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-brand-600" />
                  Key Metrics
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-slate-600">Previous Cost</span>
                    <span className="font-bold text-slate-900">$650/mo</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-slate-600">New Cost</span>
                    <span className="font-bold text-green-600">$390/mo</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-slate-600">Monthly Savings</span>
                    <span className="font-bold text-green-600">$260</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-slate-600">Annual Savings</span>
                    <span className="font-bold text-green-600">$3,120</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Sales Increase</span>
                    <span className="font-bold text-green-600">47%</span>
                  </div>
                </div>
              </div>

              {/* Features Used */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-brand-600" />
                  Features Used
                </h3>
                <div className="space-y-3">
                  {[
                    'Unified Inbox',
                    'Content Lab',
                    'Zyra AI',
                    'Social Media Management',
                    'Advanced Analytics',
                    'AI Voice',
                    'Knowledge Base',
                    '24/7 Support',
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Related Case Studies */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Related Resources</h3>
                <div className="space-y-4">
                  <Link to="/pricing" className="block group">
                    <div className="flex items-center gap-3 text-slate-700 group-hover:text-brand-600 transition">
                      <DollarSign className="h-5 w-5" />
                      <span className="font-medium">View Pricing Plans</span>
                    </div>
                  </Link>
                  <Link to="/content-lab" className="block group">
                    <div className="flex items-center gap-3 text-slate-700 group-hover:text-brand-600 transition">
                      <Share2 className="h-5 w-5" />
                      <span className="font-medium">Learn About Content Lab</span>
                    </div>
                  </Link>
                  <Link to="/social-media-management" className="block group">
                    <div className="flex items-center gap-3 text-slate-700 group-hover:text-brand-600 transition">
                      <Users className="h-5 w-5" />
                      <span className="font-medium">Social Media Management</span>
                    </div>
                  </Link>
                  <Link to="/book-a-demo" className="block group">
                    <div className="flex items-center gap-3 text-slate-700 group-hover:text-brand-600 transition">
                      <Calendar className="h-5 w-5" />
                      <span className="font-medium">Book a Demo</span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}